import { createAsyncThunk } from '@reduxjs/toolkit';
//import Config from '../../../Config';
import { showAppLoader, hideAppLoader } from '../../Shared/Loader/LoaderSlice';
import ProviderManager from '../../../provider/providerManager';
import EpiContext from '@episerver/spa-core/Spa';
import { setMagicLinkParameters, createOtpLoginStep1, createOtpLoginStep02 } from '../../../utilities/authentication/authenticationSlice';
import authenticationProvider from '../../../utilities/authentication/authenticationProvider';
import { rolesKeys } from '../OTPLoginBlock/Assets/variables';
import Config from '../../../Config';
import { goToPage, goToPageWithoutHistory, resetAuthOtpTickets, resetSecurityOtpTickets, setLoginDetails, setOrderDetails, setCheckUserResponse, setErrorPage, setCellcomAppDetails } from './EsimUpgradeBlockSlice';
import { ESIM_UPGRADE_RETURN_FROM_QR_KEY, ESIM_UPGRADE_RETURN_URL_KEY } from './esimUpgradeNavigationConstants';
import { getOnboardingSubs } from 'app/Components/Page/SelfCarePage/SelfCareAsyncThunk.js';

export const isContactPerson = (userDetails) =>
  !!(
    userDetails?.IsPrimaryContact ||
    userDetails?.IsContactPerson ||
    userDetails?.LoginData?.IsPrimaryContact ||
    userDetails?.LoginData?.IsContactPerson
  );

// זיהוי סוג login מקומי לבלוק — תומך ב-LoginType עם סיומת (למשל OTPSUBSCRIBER_SUBSCRIBER)
// בלי לשנות את determineLoginType הגלובלי של האתר
const getLoginRoles = (loginData) => {
  const saved = authenticationProvider.getLoginData();
  return [
    ...(loginData?.userDetails?.RoleList || []),
    ...(saved?.userDetails?.RoleList || []),
  ].map((role) => String(role).toUpperCase());
};

const getEsimLoginType = (loginData) => {
  const saved = authenticationProvider.getLoginData();
  const fromSaved = saved?.userDetails?.LoginData?.LoginType;
  if (fromSaved) return String(fromSaved).toUpperCase();

  const roles = getLoginRoles(loginData);
  if (isContactPerson(loginData?.userDetails)) {
    const contactRole = roles.find((r) => r.includes('CONTACT') || r.includes(rolesKeys.OTPCUSTOMER));
    if (contactRole) return contactRole;
  }

  const role = roles.find((r) =>
    (r.includes(rolesKeys.OTPSUBSCRIBER) && r.endsWith(rolesKeys.SUBSCRIBER)) ||
    r.includes(rolesKeys.OTPEXTENDED) ||
    r.includes(rolesKeys.OTPCUSTOMER),
  );
  if (role) return role;

  return String(loginData?.userDetails?.loginSubType || '').toUpperCase();
};

const isSubscriberLogin = (loginData) => {
  if (isContactPerson(loginData?.userDetails)) return false;

  const loginType = getEsimLoginType(loginData);
  if (loginType.includes('CONTACT') || loginType === rolesKeys.OTPEXTENDED) return false;
  if (loginType.endsWith(rolesKeys.SUBSCRIBER) && loginType.includes(rolesKeys.OTPSUBSCRIBER)) return true;

  return getLoginRoles(loginData).some((role) =>
    role.endsWith(rolesKeys.SUBSCRIBER) &&
    role.includes(rolesKeys.OTPSUBSCRIBER) &&
    !role.includes('CONTACT'),
  );
};

const hasValidToken = (loginData) => {
  const token = loginData?.token || authenticationProvider.getToken();
  return typeof token === 'string' && token.length > 0;
};

// OTP שני הושלם — רק LoginType עם סיומת (למשל OTPEXTENDED_CONTACTPRIVATE)
// LoginType "OTPEXTENDED" לבד אחרי OTP ראשון עדיין דורש OTP שני באשף
const isEsimSecurityComplete = (loginData) => {
  const loginType = getEsimLoginType(loginData);
  if (!loginType.includes(rolesKeys.OTPEXTENDED)) return false;
  return loginType.length > rolesKeys.OTPEXTENDED.length;
};

// OTP מלא ללקוח (למשל OTPCUSTOMER_CONTACTPRIVATE)
const isEsimFullOtpCustomerLogin = (loginType) =>
  loginType === rolesKeys.OTPCUSTOMER ||
  loginType.startsWith(`${rolesKeys.OTPCUSTOMER}_`);

// לוגין מלא עם שם משתמש/סיסמה (CREDENTIALS / NEWCREDENTIALS)
const isEsimCredentialsLogin = (loginType) =>
  loginType === rolesKeys.CREDENTIALS ||
  loginType.startsWith(`${rolesKeys.NEWCREDENTIALS}`);

const isEsimFullCustomerOrCredentialsAuth = (loginType) =>
  isEsimFullOtpCustomerLogin(loginType) || isEsimCredentialsLogin(loginType);

const isEsimFullAuth = (loginData) => {
  if (loginData?.isSilentOtpAuthenticated) return true;
  if (isEsimSecurityComplete(loginData)) return true;
  if (loginData?.isFullAuthenticated) return true;
  return isEsimFullCustomerOrCredentialsAuth(getEsimLoginType(loginData));
};

const isEsimPartialOtpAuth = (loginData) => {
  if (isEsimFullAuth(loginData)) return false;
  return !!(loginData?.isOtpAuthenticated || loginData?.isOtpExtendedAuthenticated);
};

const hasPartialOtpAuth = (loginData) => isEsimPartialOtpAuth(loginData);


const needsSubscriberSelection = (loginData) => {
  if (isSubscriberLogin(loginData)) return false;
  if (isContactPerson(loginData?.userDetails)) return true;
  return !!(
    loginData?.isFullAuthenticated ||
    loginData?.isSilentOtpAuthenticated
  );
};

const isEsimSubscriberOtpLogin = (loginData) => {
  const loginType = getEsimLoginType(loginData);
  return loginType.includes(rolesKeys.OTPSUBSCRIBER) && !needsSubscriberSelection(loginData);
};


const isEsimFullOrExtendedContactAuth = (loginData) => {
  const loginType = getEsimLoginType(loginData);
  return (
    loginData?.isFullAuthenticated ||
    loginData?.isOtpExtendedAuthenticated ||
    isEsimFullCustomerOrCredentialsAuth(loginType)
  );
};

const needsSecurityOtp = (loginData) => {
  if (isEsimSecurityComplete(loginData)) return false;
  return isEsimFullOrExtendedContactAuth(loginData);
};


const shouldSkipSecurityOtpForMagicLink = (magicLinkParameters) => {
  if (!magicLinkParameters || typeof magicLinkParameters !== 'object') return false;

  const hasMlSection = Object.keys(magicLinkParameters).some(
    (key) => magicLinkParameters[key] != null && String(magicLinkParameters[key]).trim() !== '',
  );
  if (!hasMlSection) return false;

  const clientName = String(magicLinkParameters.clientName || '').toUpperCase();
  return (
    clientName === 'JET' ||
    clientName === 'SAP' ||
    clientName === 'WONDERFUL' ||
    clientName === 'SIMPLE_GOLAN'
  );
};

// DEV ONLY — set to false / remove before commit
// const DEV_JUMP_TO_SUCCESS_PAGE = true;

export const OtpStepType = {
  LOGIN: 'OTPLOGIN',
  SECURITY: 'OTPSECURITY',
};

export const OtpValidateError = {
  WRONG_CODE: 'WRONG_CODE',
  WRONG_RESTRICT_CODE: 'WRONG_RESTRICT_CODE',
  EXPIRED_CODE: 'EXPIRED_CODE',
  GENERAL_ERROR: 'GENERAL_ERROR',
};

const OTP_ERROR_MESSAGES = {
  [OtpValidateError.WRONG_CODE]: 'הקוד שהוקלד שגוי. שננסה שוב?',
  [OtpValidateError.WRONG_RESTRICT_CODE]: 'יש טעות בקוד, שננסה שוב?',
  [OtpValidateError.EXPIRED_CODE]: 'קוד שקיבלתם כבר לא בתוקף. כדי לשמור על הפרטים שלכם, נתחיל את התהליך מחדש',
  [OtpValidateError.GENERAL_ERROR]: 'אירעה שגיאה, אנא נסה שוב.',
};

export const isRestrictedCodeRequired = (body) => {
  const codeError = parseInt(body?.codeError, 10);
  return codeError === 31 || codeError === 75 || body?.resultMessage === 'OTP_RESTRICT_CODE_REQUIRE';
};

const mapOtpValidationError = (payload) => {
  const code = String(payload?.codeError ?? '');
  if (code === '36') return OtpValidateError.WRONG_RESTRICT_CODE;
  if (code === '1005') return OtpValidateError.WRONG_CODE;
  if (code === '1003') return OtpValidateError.EXPIRED_CODE;
  return OtpValidateError.GENERAL_ERROR;
};

export const getOtpValidationErrorMessage = (result) => {
  const errorType = result?.payload?.errorType || OtpValidateError.GENERAL_ERROR;
  return OTP_ERROR_MESSAGES[errorType] || OTP_ERROR_MESSAGES[OtpValidateError.GENERAL_ERROR];
};

const normalizePhone = (phone) => String(phone || '').replace(/\D/g, '');

const pickPrimaryPhoneFromList = (userDetails) => {
  const list = userDetails?.PrimaryPhoneList?.PrimaryPhone;
  if (!list) return '';
  const items = Array.isArray(list) ? list : [list];
  for (const item of items) {
    const phone = normalizePhone(
      item?.PhoneNumber ?? item?.phoneNumber ?? item?.Subscriber ?? item?.SubscriberNumber ?? item?.Value,
    );
    if (phone) return phone;
  }
  return '';
};

// OTP אבטחה: למנוי → ContactValue (בעל חשבון); לאיש קשר → המספר שלו
export const getSecurityOtpTargetPhone = (authentication, loginDetails) => {
  const userDetails = authentication?.loginData?.userDetails;

  if (isContactPerson(userDetails)) {
    return userDetails?.contactNumber || userDetails?.LoginData?.ContactValue || '';
  }

  const savedLogin = authenticationProvider.getLoginData();
  const rawLoginData = userDetails?.LoginData || savedLogin?.userDetails?.LoginData || {};
  const rawUserDetails = userDetails?.LoginData ? userDetails : savedLogin?.userDetails || {};

  // כמו OtpExtendedPopup — תמיד מספר בעל החשבון
  const contactValue = rawLoginData.ContactValue || userDetails?.contactNumber;
  if (contactValue) return contactValue;

  const subscriberNo = normalizePhone(loginDetails?.subscriberNo);
  const otpNumber = rawLoginData.OtpNumber || userDetails?.OtpNumber;
  if (otpNumber && normalizePhone(otpNumber) !== subscriberNo) return otpNumber;

  const primaryPhone = pickPrimaryPhoneFromList(rawUserDetails);
  if (primaryPhone && primaryPhone !== subscriberNo) return primaryPhone;

  return '';
};

const goToSecurityOtpPage = (thunkAPI) => {
  // לא לקבוע securityOtpPhone מראש מ-ContactValue — המספר האמיתי מגיע מ-OtpExtendedStep1
  thunkAPI.dispatch(resetSecurityOtpTickets());
  thunkAPI.dispatch(goToPageWithoutHistory('securityOtpPage'));
};

export const prepareBlockAsync = createAsyncThunk('esimUpgradeBlock/prepareBlockAsync', async (data, thunkAPI) => {
  // if (DEV_JUMP_TO_SUCCESS_PAGE) {
  //   thunkAPI.dispatch(setLoginDetails({ subscriberNo: '0526592434' }));
  //   thunkAPI.dispatch(goToPage('successPage'));
  //   return;
  // }

  const authentication = thunkAPI.getState().authentication;

  try {
    if (typeof window !== 'undefined' && sessionStorage.getItem(ESIM_UPGRADE_RETURN_FROM_QR_KEY)) {
      sessionStorage.removeItem(ESIM_UPGRADE_RETURN_FROM_QR_KEY);
      sessionStorage.removeItem(ESIM_UPGRADE_RETURN_URL_KEY);
    }

    const magicLinkParameters = authentication.loginData?.magicLinkParameters;

    if (
      (magicLinkParameters?.scopeName?.toUpperCase() == 'ESIMPROVIDENP' ||
        magicLinkParameters?.scopeName?.toUpperCase() == 'ESIMPROVIDENPIDF') &&
      !authentication?.loginData?.userDetails?.IDNumber
    ) {
      thunkAPI.dispatch(setErrorPageAsync({ error: 'NIUD_NOT_FINISH_ERROR' }));
      return;
    }

    const loginData = authentication.loginData;

    const phoneNumber = magicLinkParameters?.phoneNumber;
    if (phoneNumber) {
      thunkAPI.dispatch(setLoginDetails({ subscriberNo: phoneNumber }));
    }

    if (needsSubscriberSelection(loginData)) {
      if (phoneNumber) {
        await thunkAPI.dispatch(checkUserAfterLoginAsync({ isDisplayLoader: true }));
      } else {
        await thunkAPI.dispatch(getSubscribersAsync());
      }
      return;
    }

    if (hasValidToken(loginData) && (isSubscriberLogin(loginData) || hasPartialOtpAuth(loginData))) {
      // יש ML.phoneNumber — לא לדרוס ב-ContactValue/otpPhone
      if (!phoneNumber) {
        const saved = authenticationProvider.getLoginData();
        const subscriberNo = normalizePhone(
          loginData?.userDetails?.OtpNumber ||
          saved?.userDetails?.LoginData?.OtpNumber ||
          loginData?.userDetails?.contactNumber,
        );
        if (subscriberNo) {
          thunkAPI.dispatch(setLoginDetails({ subscriberNo }));
        }
      }
      if (shouldSkipSecurityOtpForMagicLink(magicLinkParameters)) {
        await thunkAPI.dispatch(checkUserAfterLoginAsync({ isDisplayLoader: true }));
        return;
      }
      goToSecurityOtpPage(thunkAPI);
      return;
    }
    thunkAPI.dispatch(goToPageWithoutHistory('otpPage'));
  } catch (error) {
  } finally {
  }
});


export const createOtpAsync = createAsyncThunk('esimUpgradeBlock/createOtpAsync', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());

    const result = await thunkAPI.dispatch(
      createOtpLoginStep1({
        phoneNumber: data.subscriberNo,
        isExtended: !!data.isExtendedOtp,
      }),
    );
    return result?.payload;
  } catch (error) {
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});


export const validateOtpAsync = createAsyncThunk('esimUpgradeBlock/validateOtpAsync', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());

    const step02Params = data.isExtendedOtp
      ? {
        otpCode: data.code,
        phoneNumber: data.subscriberNo,
        guid: data.otpTicketId,
        restrictCode: data.restrictCode || '',
        loginType: rolesKeys.OTPEXTENDED,
        checkPhoneNumber: data.securityOtpPhone,
        termsAccepted: true,
      }
      : {
        otpCode: data.code,
        phoneNumber: data.subscriberNo,
        guid: data.otpTicketId,
        restrictCode: data.restrictCode || '',
      };

    const result = await thunkAPI.dispatch(createOtpLoginStep02(step02Params));

    if (result.type.includes('rejected')) {
      return { success: false, errorType: OtpValidateError.GENERAL_ERROR };
    }

    if (result.payload?.isSuccess) {
      return { success: true };
    }

    return { success: false, errorType: mapOtpValidationError(result.payload) };
  } catch (error) {
    return { success: false, errorType: OtpValidateError.GENERAL_ERROR };
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});


export const getPriceEsimByUserParametersAsync = createAsyncThunk(
  'esimUpgradeBlock/getPriceEsimByUserParametersAsync',
  async (data, thunkAPI) => {
    try {
      //thunkAPI.dispatch(showAppLoader());
      const state = thunkAPI.getState().esimUpgradeBlock;
      const authentication = thunkAPI.getState().authentication;
      const magicLinkParameters = authentication.loginData?.magicLinkParameters;
      const isJet = state.checkUserResponse.isJet || magicLinkParameters?.sourceSystem == 'JET';

      const params = {
        personalInfo: {
          isJet: isJet,
          phoneNumberToReplace: state.loginDetails.subscriberNo,
          idNumber: state.checkUserResponse.idNumber,
        },
        epiInfo: {
          upgCourse: state.esimBlockData.upgCourse,
        },
      };

      const result = await ProviderManager.getProviderData('getPriceEsimByUserParameters', params);
      const isSuccess = result?.Header?.ReturnCode == 0;

      if (isSuccess) {
        thunkAPI.dispatch(goToPage('replaceSimToEsimPage'));
      } else {
        thunkAPI.dispatch(setErrorPageAsync({ error: 'GET_PRICE_ERROR' }));
      }

      return result;
    } catch (error) {
      thunkAPI.dispatch(setErrorPageAsync({ error: 'GET_PRICE_ERROR' }));
    } finally {
      //thunkAPI.dispatch(hideAppLoader());
    }
  },
);

export const checkUserAfterLoginAsync = createAsyncThunk('esimUpgradeBlock/checkUserAfterLoginAsync', async (data, thunkAPI) => {
  try {
    const state = thunkAPI.getState().esimUpgradeBlock;
    const authentication = thunkAPI.getState().authentication;
    const magicLinkParameters = authentication.loginData?.magicLinkParameters;

    if (magicLinkParameters?.clientName?.toUpperCase() == 'TALKMAN') {
      if (magicLinkParameters?.orderNumber != '') {
        thunkAPI.dispatch(goToPage('replaceSimToEsimPage'));
      } else {
        thunkAPI.dispatch(setErrorPageAsync({ error: 'ORDER_NUMBER_ERROR' }));
      }
      return;
    }

    if (data?.isDisplayLoader) {
      thunkAPI.dispatch(showAppLoader());
    }

    const params = {
      subscriberNo: state.loginDetails.subscriberNo,
    };

    const result = await ProviderManager.getProviderData('checkUserAfterLogin', params);
    //const isSuccess = (result?.Header?.ReturnCode == 0 && result?.Body?.utcInd == 'Y');
    const isSuccess = result?.Header?.ReturnCode == 0 && result?.Body?.successOrder?.isSuccess;

    if (!isSuccess) {
      if (magicLinkParameters?.scopeName == 'ESIMPROVIDENP' || magicLinkParameters?.scopeName == 'ESIMPROVIDENPIDF') {
        thunkAPI.dispatch(setErrorPageAsync({ error: 'NIUD_NOT_FINISH_ERROR' }));
      } else {
        const codeError = result?.Body?.successOrder?.codeError;
        if (codeError == '1428102') {
          thunkAPI.dispatch(setErrorPageAsync({ error: 'CHECK_USER_ERROR_BUSINESS_USER' }));
        } else {
          thunkAPI.dispatch(setErrorPageAsync({ error: 'CHECK_USER_ERROR' }));
        }
      }
      return;
    }

    const response = {
      isJet: result.Body?.isJet,
      primaryEmail: result.Body?.primaryEmail,
      idNumber: result.Body?.idNumber,
      firstName: result.Body?.firstName,
      lastName: result.Body?.lastName,
      isBusiness: result.Body?.isBusiness,
    };

    thunkAPI.dispatch(setCheckUserResponse(response));

    const clientName = magicLinkParameters?.clientName || 'CELLCOM_WEB_SITE';

    switch (clientName.toUpperCase()) {
      case 'CELLBUS_WEB_SITE':
      case 'CELLCOM_WEB_SITE':
      case 'JET':
      case 'WONDERFUL':
      case 'SIMPLE_GOLAN':
        await thunkAPI.dispatch(getPriceEsimByUserParametersAsync());
        break;
      case 'SAP':
      case 'IDFPRT':
        if (magicLinkParameters?.orderNumber != '') {
          thunkAPI.dispatch(goToPage('replaceSimToEsimPage'));
        } else {
          thunkAPI.dispatch(setErrorPageAsync({ error: 'ORDER_NUMBER_ERROR' }));
        }
        break;
      default:
        // clientName לא מוכר — לא דף לבן: OTP אבטחה; אחרי OTP שני ממשיכים לתמחור
        if (data?.fromSecurityOtp) {
          await thunkAPI.dispatch(getPriceEsimByUserParametersAsync());
        } else {
          goToSecurityOtpPage(thunkAPI);
        }
        break;
    }

    return isSuccess;
  } catch (error) {
    thunkAPI.dispatch(setErrorPageAsync({ error: 'CHECK_USER_ERROR' }));
  } finally {
    if (data?.isDisplayLoader) {
      thunkAPI.dispatch(hideAppLoader());
    }
  }
});


export const proceedAfterFirstOtpValidatedAsync = createAsyncThunk(
  'esimUpgradeBlock/proceedAfterFirstOtpValidatedAsync',
  async (_, thunkAPI) => {
    const loginData = thunkAPI.getState().authentication.loginData;
    const userDetails = loginData?.userDetails;
    const magicLinkParameters = loginData?.magicLinkParameters;

    if (!isContactPerson(userDetails)) {
      if (shouldSkipSecurityOtpForMagicLink(magicLinkParameters)) {
        await thunkAPI.dispatch(checkUserAfterLoginAsync({ isDisplayLoader: true }));
        return;
      }
      goToSecurityOtpPage(thunkAPI);
    } else {
      await thunkAPI.dispatch(getSubscribersAsync());
    }
  },
);

export const proceedAfterSecurityOtpValidatedAsync = createAsyncThunk(
  'esimUpgradeBlock/proceedAfterSecurityOtpValidatedAsync',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userDetails = state.authentication.loginData?.userDetails;
    const subscriberNo = state.esimUpgradeBlock.loginDetails?.subscriberNo;

    // אחרי OTP שני בלוגין מורחב — ישר לבחירת מנוי; אחרי בחירת מנוי — ממשיכים בתהליך
    if (isContactPerson(userDetails) && !subscriberNo) {
      await thunkAPI.dispatch(getSubscribersAsync());
    } else {
      await thunkAPI.dispatch(checkUserAfterLoginAsync({ isDisplayLoader: true, fromSecurityOtp: true }));
    }
  },
);

export const proceedAfterSubscriberSelectedAsync = createAsyncThunk(
  'esimUpgradeBlock/proceedAfterSubscriberSelectedAsync',
  async (data, thunkAPI) => {
    const state = thunkAPI.getState();
    const loginData = state.authentication.loginData;
    const magicLinkParameters = loginData?.magicLinkParameters;

    if (needsSecurityOtp(loginData) && !shouldSkipSecurityOtpForMagicLink(magicLinkParameters)) {
      goToSecurityOtpPage(thunkAPI);
    } else {
      await thunkAPI.dispatch(checkUserAfterLoginAsync({ isDisplayLoader: data?.isDisplayLoader }));
    }
  },
);

export const getSubscribersAsync = createAsyncThunk('esimUpgradeBlock/getSubscribersAsync', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());

    let subscriberList = [];

    try {
      const cellularResult = await ProviderManager.getProviderData('getCustomerCellularList', []);
      const cellularList = cellularResult?.Body;
      if (Array.isArray(cellularList) && cellularList.length > 0) {
        subscriberList = cellularList.map((sub) => sub.subscriberNo).filter(Boolean);
      }
    } catch (error) {
    }

    if (subscriberList.length === 0) {
      const result = await thunkAPI.dispatch(getOnboardingSubs());
      subscriberList = result?.payload || [];
    }

    subscriberList = [...new Set(subscriberList)];

    if (!subscriberList || subscriberList.length == 0) {
      thunkAPI.dispatch(setErrorPageAsync({ error: 'GET_SUBSCRIBERS_ERROR' }));
      return;
    }

    if (subscriberList.length == 1) {
      thunkAPI.dispatch(setLoginDetails({ subscriberNo: subscriberList[0] }));
      await thunkAPI.dispatch(proceedAfterSubscriberSelectedAsync());
    } else {
      thunkAPI.dispatch(goToPageWithoutHistory('chooseSubscriberPage'));
    }

    return subscriberList;
  } catch (error) {
    thunkAPI.dispatch(setErrorPageAsync({ error: 'GET_SUBSCRIBERS_ERROR' }));
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});


export const goToInstructionsPageAsync = createAsyncThunk('esimUpgradeBlock/goToInstructionsPageAsync', async (data, thunkAPI) => {
  const state = thunkAPI.getState().esimUpgradeBlock;
  const email = state.checkUserResponse.isJet && state.checkUserResponse.primaryEmail ? state.checkUserResponse.primaryEmail : state.email;

  const magicLink = {
    phoneNumber: state.loginDetails.subscriberNo,
    email: email,
    otpPhone: '',
    orderNumber: state.orderDetails.orderId,
    toActivate: 'Y',
    sourceSystem: 'JET',
    clientName: '',
    scopeName: 'ESIMUPGRADEQRCODE',
    reasonChg: '',
  };

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(ESIM_UPGRADE_RETURN_URL_KEY, window.location.pathname + window.location.search);
  }

  thunkAPI.dispatch(setMagicLinkParameters(magicLink));
  EpiContext.navigateTo(state.esimBlockData.instructionsPageUrl);
});


const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))


export const createEsimOrderAsync = createAsyncThunk('esimUpgradeBlock/createEsimOrderAsync', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const state = thunkAPI.getState().esimUpgradeBlock;

    //upgCourse|productId|package|combSeq
    // const email = (state.checkUserResponse.isJet && state.checkUserResponse.primaryEmail ? state.checkUserResponse.primaryEmail : state.email);

    const params = {
      personalInfo: {
        phoneNumberToReplace: state.loginDetails.subscriberNo,
        email: state.email,
        primaryEmail: state.checkUserResponse.primaryEmail,
        idNumber: state.checkUserResponse?.idNumber || '',
        firstName: state.checkUserResponse?.firstName || '',
        lastName: state.checkUserResponse?.lastName || '',
        isJet: state.checkUserResponse.isJet,
      },
      epiInfo: {
        urlActivateEsimPage: state.esimBlockData.instructionsPageUrl,
        upgCourse: state.esimBlockData.upgCourse,
        productId: state.esimBlockData.productId,
        package: state.esimBlockData.package,
        combSeq: state.esimBlockData.combSeq,
      },
    };

    const result = await ProviderManager.getProviderData('createEsimOrder', params);
    const isSuccess = result?.Header?.ReturnCode == 0 && result?.Body?.isSuccess;
    const orderId = result?.Body?.orderId;

    thunkAPI.dispatch(setOrderDetails({ orderId: orderId }));

    if (!isSuccess) {
      const codeError = result?.Body?.codeError;
      if (codeError == '1869201') {
        //exists order
        thunkAPI.dispatch(goToPage('successPage'));
      } else {
        thunkAPI.dispatch(setErrorPageAsync({ error: 'CREATE_ESIM_ORDER_ERROR' }));
      }
      return;
    }

    if (!orderId) {
      thunkAPI.dispatch(setErrorPageAsync({ error: 'NO_ORDER_ERROR' }));
      return;
    }
    const isCellcomAppActive = result?.Body?.isCellcomAppActive;
    const tinyAppUrl = result?.Body?.resultByOrders?.[0]?.magicLinkResponse?.[0]?.tinyAppUrl;

    thunkAPI.dispatch(setCellcomAppDetails({ isCellcomAppActive, tinyAppUrl }));

    await sleep(3000);
    thunkAPI.dispatch(goToPage('successPage'));

    return result;
  } catch (error) {
    thunkAPI.dispatch(setErrorPageAsync({ error: 'CREATE_ESIM_ORDER_ERROR' }));
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});


export const setErrorPageAsync = createAsyncThunk('esimUpgradeBlock/setErrorPageAsync', async (data, thunkAPI) => {
  thunkAPI.dispatch(showAppLoader());

  /*
          GET_PRICE_ERROR
          CHECK_USER_ERROR
          ORDER_NUMBER_ERROR
          CHECK_USER_ERROR
          CHECK_USER_ERROR_BUSINESS_USER
          GET_SUBSCRIBERS_ERROR
          CREATE_ESIM_ORDER_ERROR
          NO_ORDER_ERROR
          ERROR_OPEN_CHAT_ERROR
          NIUD_NOT_FINISH_ERROR
      */

  const params = { errorDescription: data.error };
  thunkAPI.dispatch(setErrorPage(params));

  thunkAPI.dispatch(hideAppLoader());
});


export const openWhatsAppChatAsync = createAsyncThunk('esimUpgradeBlock/openWhatsAppChatAsync', async (data, thunkAPI) => {
  thunkAPI.dispatch(showAppLoader());

  try {
    const state = thunkAPI.getState().esimUpgradeBlock;

    // const whatsAppResult = await thunkAPI.dispatch(checkIfWhatsAppActiveAsync());
    // const isWhatsAppActive = whatsAppResult?.payload?.Body;

    const isWhatsAppActive = true; // 24\7
    if (!isWhatsAppActive) {
      thunkAPI.dispatch(setErrorPageAsync({ error: 'ERROR_OPEN_CHAT_ERROR' }));
      return;
    }

    var params = {
      phoneNumber: state.loginDetails.subscriberNo,
      fullAddress: '',
      messageText: 'פנייה בתהליך ESIM',
    };

    const result = await ProviderManager.getProviderData('esimOpenWhatsApp', params);
    const isSuccess = result?.Header?.ReturnCode == 0 && result?.Body?.isSuccess;

    if (isSuccess) {
      thunkAPI.dispatch(goToPage('whatsAppSuccessPage'));
    } else {
      thunkAPI.dispatch(setErrorPageAsync({ error: 'ERROR_OPEN_CHAT_ERROR' }));
    }

    return result;
  } catch (error) {
    thunkAPI.dispatch(setErrorPageAsync({ error: 'ERROR_OPEN_CHAT_ERROR' }));
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});


const clearAuthAndResetOtpTickets = async (dispatch) => {
  await authenticationProvider.clearAuthenticationData();
  dispatch(resetLoginData());
  dispatch(resetAuthOtpTickets());
};

export const navigateEsimUpgradeBack = () => async (dispatch, getState) => {
  dispatch(hideAppLoader());
  const state = getState();
  const { page, subscriberList } = state.esimUpgradeBlock;
  const loginData = state.authentication.loginData;
  const isContact = isContactPerson(loginData?.userDetails);

  if (page === 'otpPage') {
    window.location.href = Config.lobbyUrl;
    return;
  }

  switch (page) {
    case 'validateOtpPage': {
      dispatch(resetAuthOtpTickets());
      dispatch(goToPageWithoutHistory('otpPage'));
      return;
    }
    case 'chooseSubscriberPage': {
      if (hasPartialOtpAuth(loginData) || loginData?.isFullAuthenticated || loginData?.isSilentOtpAuthenticated) {
        window.location.href = Config.lobbyUrl;
        return;
      }
      dispatch(resetAuthOtpTickets());
      dispatch(goToPageWithoutHistory('otpPage'));
      return;
    }
    case 'securityOtpPage': {
      if (isContact && subscriberList?.length > 1) {
        dispatch(resetSecurityOtpTickets());
        dispatch(goToPageWithoutHistory('chooseSubscriberPage'));
        return;
      }
      if (hasPartialOtpAuth(loginData) || loginData?.isFullAuthenticated || loginData?.isSilentOtpAuthenticated) {
        window.location.href = Config.lobbyUrl;
        return;
      }
      dispatch(resetAuthOtpTickets());
      dispatch(goToPageWithoutHistory('otpPage'));
      return;
    }
    case 'identityValidationPage': {
      dispatch(resetAuthOtpTickets());
      dispatch(goToPageWithoutHistory('otpPage'));
      return;
    }
    case 'replaceSimToEsimPage': {
      if (isContact && subscriberList?.length > 1) {
        dispatch(goToPageWithoutHistory('chooseSubscriberPage'));
        return;
      }
      dispatch(goToPageWithoutHistory('securityOtpPage'));
      return;
    }
    default:
      window.history.back();
  }
};

export const createMemoAsync = createAsyncThunk('esimUpgradeBlock/createMemoAsync', async (data, thunkAPI) => {
  try {
    const params = {};
    ProviderManager.getProviderData('createMemoIntCr', params);
  } catch {
  } finally {
  }
});


export const sendSecondMagicLink = createAsyncThunk('esimBlock/sendSecondMagicLink', async (data, thunkAPI) => {
  try {
    const state = thunkAPI.getState().esimBlock;
    const request = {
      personalInfo: {
        eMail: state.email,
      },
    };

    ProviderManager.getProviderData('sendSecondMagicLink', request);
  } catch { }
});
